import React, { useState } from "react";
import { Check, Link2, Mail } from "lucide-react";
import { track } from "../../seo/track";
import { shareUrl } from "../../utils/referral";

const LABELS = {
  en: { heading: "Share this page", copy: "Copy link", copied: "Copied", subject: "Form 2290" },
  es: { heading: "Compartir esta página", copy: "Copiar enlace", copied: "Copiado", subject: "Formulario 2290" },
};

/**
 * "Share this guide" row for public pages (blog posts, Spanish guides, calculators, reject
 * codes). Shares the page itself - no referral code, no endorsement text - so a visitor can
 * pass a useful page to their group. `path` is the canonical path with its trailing slash.
 */
export default function ShareRow({ path, title, lang = "en" }) {
  const t = LABELS[lang];
  const [copied, setCopied] = useState(false);
  const url = (medium) => shareUrl(path, { medium, campaign: "page-share" });
  const enc = encodeURIComponent;
  const onShare = (channel) => track("share_click", { channel, placement: "page", page: path });

  const copy = async () => {
    onShare("copy");
    try {
      await navigator.clipboard.writeText(url("copy"));
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setCopied(false);
    }
  };

  const links = [
    { id: "whatsapp", label: "WhatsApp", href: `https://wa.me/?text=${enc(`${title} ${url("whatsapp")}`)}` },
    { id: "facebook", label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url("facebook"))}` },
    { id: "x", label: "X", href: `https://x.com/intent/post?text=${enc(title)}&url=${enc(url("x"))}` },
    { id: "linkedin", label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url("linkedin"))}` },
    { id: "email", label: "Email", href: `mailto:?subject=${enc(t.subject)}&body=${enc(`${title} ${url("email")}`)}`, Icon: Mail },
  ];

  const btn =
    "inline-flex items-center gap-1.5 h-9 px-3 rounded-[10px] border border-[var(--line-2)] bg-[var(--panel)] text-[13px] font-semibold text-[var(--ink)] no-underline hover:border-[var(--accent)] transition-colors";

  return (
    <div className="mt-10 pt-6 border-t border-[var(--line)]">
      <div className="text-[14px] font-bold text-[var(--ink)] mb-2.5">{t.heading}</div>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={copy} className={btn}>
          {copied ? <Check size={15} /> : <Link2 size={15} />} {copied ? t.copied : t.copy}
        </button>
        {links.map(({ id, label, href, Icon }) => (
          <a
            key={id}
            href={href}
            target={id === "email" ? undefined : "_blank"}
            rel="noopener noreferrer"
            onClick={() => onShare(id)}
            className={btn}
          >
            {Icon && <Icon size={15} />} {label}
          </a>
        ))}
      </div>
    </div>
  );
}
